import { useState } from 'react';
import Layout from '../../../components/feature/Layout';

interface Movement {
  id: string;
  date: string;
  type: 'cargo' | 'abono';
  concept: string;
  reference: string;
  amount: number;
  balance: number;
}

interface ClientSummary {
  id: string;
  name: string;
  totalBalance: number;
  overdueBalance: number;
  pendingInvoices: number;
  creditLimit: number;
  creditDays: number;
}

export default function StatementsPage() {
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState<Movement | null>(null);

  const clients: ClientSummary[] = [
    {
      id: '1',
      name: 'Importadora Global S.A.',
      totalBalance: 45000,
      overdueBalance: 12000,
      pendingInvoices: 5,
      creditLimit: 100000,
      creditDays: 30
    },
    {
      id: '2',
      name: 'Exportadora del Pacífico',
      totalBalance: 28500,
      overdueBalance: 0,
      pendingInvoices: 3,
      creditLimit: 75000,
      creditDays: 45
    },
    {
      id: '3',
      name: 'Logística Continental',
      totalBalance: 67800,
      overdueBalance: 23000,
      pendingInvoices: 8,
      creditLimit: 150000,
      creditDays: 30
    }
  ];

  const movements: Movement[] = [
    {
      id: '1',
      date: '2024-01-15',
      type: 'cargo',
      concept: 'Factura OP-2024-0145',
      reference: 'FAC-2024-0089',
      amount: 15000,
      balance: 45000
    },
    {
      id: '2',
      date: '2024-01-10',
      type: 'abono',
      concept: 'Pago Transferencia',
      reference: 'PAG-2024-0034',
      amount: -8000,
      balance: 30000
    },
    {
      id: '3',
      date: '2024-01-08',
      type: 'cargo',
      concept: 'Factura OP-2024-0132',
      reference: 'FAC-2024-0078',
      amount: 12000,
      balance: 38000
    },
    {
      id: '4',
      date: '2024-01-05',
      type: 'cargo',
      concept: 'Factura OP-2024-0128',
      reference: 'FAC-2024-0075',
      amount: 18000,
      balance: 26000
    },
    {
      id: '5',
      date: '2024-01-03',
      type: 'abono',
      concept: 'Pago Cheque',
      reference: 'PAG-2024-0028',
      amount: -10000,
      balance: 8000
    }
  ];

  const selectedClientData = clients.find(c => c.id === selectedClient);

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2A43]">Estados de Cuenta</h1>
          <p className="text-sm text-gray-500 mt-1">Vista financiera resumida por cliente</p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Saldo Total</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">Q 141,300</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-2xl text-blue-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Saldo Vencido</p>
                <p className="text-2xl font-bold text-red-600 mt-1">Q 35,000</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <i className="ri-alarm-warning-line text-2xl text-red-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Facturas Pendientes</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">16</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <i className="ri-file-list-3-line text-2xl text-yellow-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Clientes con Saldo</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">3</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-user-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Selector de Cliente */}
        <div className="bg-white rounded-lg shadow-[0_4px_16px_rgba(10,42,67,0.08)] p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seleccionar Cliente
          </label>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">-- Seleccione un cliente --</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>{client.name}</option>
            ))}
          </select>

          {selectedClientData && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <p className="text-sm text-gray-600">Saldo Total</p>
                <p className="text-xl font-bold text-gray-900">Q {selectedClientData.totalBalance.toLocaleString()}</p>
              </div>
              <div className="border-l-4 border-red-500 pl-4">
                <p className="text-sm text-gray-600">Saldo Vencido</p>
                <p className="text-xl font-bold text-red-600">Q {selectedClientData.overdueBalance.toLocaleString()}</p>
              </div>
              <div className="border-l-4 border-yellow-500 pl-4">
                <p className="text-sm text-gray-600">Facturas Pendientes</p>
                <p className="text-xl font-bold text-gray-900">{selectedClientData.pendingInvoices}</p>
              </div>
            </div>
          )}
        </div>

        {/* Tabla de Movimientos */}
        {selectedClient && (
          <div className="bg-white rounded-lg shadow-[0_4px_16px_rgba(10,42,67,0.08)]">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Movimientos</h2>
              <div className="flex gap-2">
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
                  <i className="ri-file-pdf-line"></i>
                  Descargar PDF
                </button>
                <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2">
                  <i className="ri-mail-send-line"></i>
                  Enviar por Email
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Concepto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referencia</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Saldo</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {movements.map((movement) => (
                    <tr key={movement.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(movement.date).toLocaleDateString('es-GT')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          movement.type === 'cargo' 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {movement.type === 'cargo' ? 'Cargo' : 'Abono'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{movement.concept}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{movement.reference}</td>
                      <td className="px-6 py-4 text-sm text-right font-medium">
                        <span className={movement.type === 'cargo' ? 'text-red-600' : 'text-green-600'}>
                          Q {Math.abs(movement.amount).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-right font-medium text-gray-900">
                        Q {movement.balance.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => {
                            setSelectedMovement(movement);
                            setShowDetailModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <i className="ri-eye-line text-lg"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal de Detalle */}
        {showDetailModal && selectedMovement && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Detalle del Movimiento</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                    <p className="text-gray-900">{new Date(selectedMovement.date).toLocaleDateString('es-GT')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      selectedMovement.type === 'cargo' 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {selectedMovement.type === 'cargo' ? 'Cargo' : 'Abono'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Concepto</label>
                  <p className="text-gray-900">{selectedMovement.concept}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referencia</label>
                  <p className="text-gray-900">{selectedMovement.reference}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monto</label>
                    <p className={`text-xl font-bold ${
                      selectedMovement.type === 'cargo' ? 'text-red-600' : 'text-green-600'
                    }`}>
                      Q {Math.abs(selectedMovement.amount).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Saldo Resultante</label>
                    <p className="text-xl font-bold text-gray-900">Q {selectedMovement.balance.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
